import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Service, inject } from '@angular/core';
import { environment } from '@env';
import { firstValueFrom } from 'rxjs';
import type { ICliente, IPermiso, IToken, IUsuario } from 'modelos';

/**
 * Llamadas crudas de autenticación, **sin pasar por los interceptores**.
 *
 * Usa `HttpBackend` y no `HttpClient` a propósito: el interceptor de
 * autenticación necesita poder refrescar el token, y si esa llamada volviera a
 * pasar por él se armaría un bucle. A cambio, acá las URLs se escriben enteras.
 *
 * El contrato es el mismo que el de IRIX — mismos endpoints, mismo `Basic
 * web:web`, mismo `application/x-www-form-urlencoded` — porque el backend no se
 * toca (ADR-008).
 */
@Service()
export class AutenticacionApi {
  /** `HttpClient` montado directo sobre el backend: cero interceptores. */
  private readonly http = new HttpClient(inject(HttpBackend));

  private get base(): string {
    return environment.api;
  }

  private cabeceraOauth(): HttpHeaders {
    return new HttpHeaders({
      Authorization: 'Basic ' + btoa('web:web'),
      'Content-Type': 'application/x-www-form-urlencoded',
    });
  }

  private cabeceraBearer(token: string, idPermiso?: string): HttpHeaders {
    let headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    if (idPermiso !== undefined) headers = headers.set('x-permiso', idPermiso);
    return headers;
  }

  login(usuario: string, clave: string): Promise<IToken> {
    const cuerpo =
      `username=${encodeURIComponent(usuario)}` +
      `&password=${encodeURIComponent(clave)}` +
      `&grant_type=password`;
    return firstValueFrom(
      this.http.post<IToken>(`${this.base}/auth/login`, cuerpo, { headers: this.cabeceraOauth() }),
    );
  }

  loginGoogle(idToken: string): Promise<IToken> {
    return firstValueFrom(this.http.post<IToken>(`${this.base}/auth/google`, { idToken }));
  }

  refrescar(refreshToken: string): Promise<IToken> {
    const cuerpo =
      `refresh_token=${encodeURIComponent(refreshToken)}` + `&grant_type=refresh_token`;
    return firstValueFrom(
      this.http.post<IToken>(`${this.base}/auth/refresh_token`, cuerpo, {
        headers: this.cabeceraOauth(),
      }),
    );
  }

  usuarioPropio(token: string): Promise<IUsuario> {
    return firstValueFrom(
      this.http.get<IUsuario>(`${this.base}/usuarios/propio`, {
        headers: this.cabeceraBearer(token),
      }),
    );
  }

  permisosMios(token: string): Promise<IPermiso[]> {
    return firstValueFrom(
      this.http.get<IPermiso[]>(`${this.base}/permisos/mios`, {
        headers: this.cabeceraBearer(token),
      }),
    );
  }

  clientePropio(token: string, idPermiso = ''): Promise<ICliente> {
    return firstValueFrom(
      this.http.get<ICliente>(`${this.base}/clientes/propio`, {
        headers: this.cabeceraBearer(token, idPermiso),
      }),
    );
  }

  /**
   * Pide el mail con el enlace de recuperación. `origen: 'faro'` hace que el
   * backend mande el mail de FARO con el enlace a FARO; sin él manda el de IRIX.
   */
  recuperarClave(email: string): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(`${this.base}/auth/recuperar_password`, { email, origen: 'faro' }),
    );
  }

  /** Canjea el token del mail por una clave nueva. */
  resetearClave(token: string, password: string): Promise<{ message: string }> {
    return firstValueFrom(
      this.http.post<{ message: string }>(`${this.base}/auth/resetear_password`, {
        token,
        password,
      }),
    );
  }
}
